import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService } from '../services/AuthService';
import { User } from '../models/types';

// Define the context type
interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAdmin: () => boolean;
  loading: boolean;
  error: string | null;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => false,
  logout: () => {},
  isAdmin: () => false,
  loading: true,
  error: null
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    console.log("Current user from localStorage:", currentUser);
    setUser(currentUser);
    setLoading(false);
  }, []);

  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    console.log(`Attempting to login with username: ${username}`);
    setLoading(true);
    setError(null);
    
    try {
      const loggedInUser = await AuthService.login(username, password);
      console.log("Login result:", loggedInUser);
      
      if (loggedInUser) {
        console.log("Login successful, setting user in context");
        setUser(loggedInUser);
        setLoading(false);
        return true;
      } else {
        console.log("Login failed: Invalid credentials");
        setError('Invalid username or password');
        setLoading(false);
        return false;
      }
    } catch (err) {
      console.error("Login error:", err);
      setError('An error occurred during login');
      setLoading(false);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    console.log("Logging out user");
    AuthService.logout();
    setUser(null);
  };

  // Check if user is admin
  const isAdmin = (): boolean => {
    return user ? user.isAdmin : false;
  };

  // Context value
  const value = {
    user,
    login,
    logout,
    isAdmin,
    loading,
    error
  };

  console.log("Auth context current state:", { user, loading, error });
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
